/**
 * PRP-WEB-1 W2.8: the in-app delete confirmation that replaced `window.confirm`. It names what
 * goes; when `typedName` is set the user must type that name before Delete is enabled (a project
 * with a priced or chat estimate). Confirming starts the Undo window (UndoDeleteToast); it does
 * not send anything itself.
 */
import { useState } from 'react';
import type { ReactNode } from 'react';

import { BackendAPI } from '../../lib/api';
import { ProjectOverviewAPI } from '../../lib/projectOverviewApi';
import { getProjectDisplayName } from '../../lib/utils';
import type { Project } from '../../types';
import { useUndoableDelete } from './UndoDeleteToast';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Modal } from './ui/modal';

export interface ConfirmDeleteDialogProps {
  /** "Delete project?" */
  title: string;
  /** What goes, in plain words. */
  children: ReactNode;
  /** When set, Delete stays disabled until this is typed (case and outer spaces ignored). */
  typedName?: string | null;
  /** Still finding out what goes: Delete stays disabled. */
  waiting?: boolean;
  onConfirm: () => void;
  onClose: () => void;
}

/** True when the typed text matches the name the dialog asks for. */
export function typedMatches(typed: string, name: string): boolean {
  return typed.trim().toLowerCase() === name.trim().toLowerCase();
}

export default function ConfirmDeleteDialog({ title, children, typedName, waiting, onConfirm, onClose }: ConfirmDeleteDialogProps) {
  const [typed, setTyped] = useState('');
  const needsName = !!typedName && typedName.trim().length > 0;
  const ok = !waiting && (!needsName || typedMatches(typed, typedName!));
  return (
    <Modal
      label={title}
      title={title}
      onClose={onClose}
      testId="confirm-delete"
      className="max-w-[440px]"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="danger" disabled={!ok} onClick={onConfirm} data-testid="confirm-delete-yes">
            Delete
          </Button>
        </>
      }
    >
      <div className="text-[13px] leading-[1.5] text-[var(--tx-mid)]">{children}</div>
      <p className="mt-2 text-[12px] text-[var(--tx-lo)]">You can undo for 10 seconds after this.</p>
      {needsName && (
        <label className="mt-3 block text-[12.5px] text-[var(--tx-mid)]">
          Type <b className="text-[var(--tx-hi)]">{typedName}</b> to confirm
          <Input
            className="mt-1.5"
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && ok) onConfirm();
            }}
            aria-label="Type the name to confirm"
            autoFocus
          />
        </label>
      )}
    </Modal>
  );
}

/** What deleting a project takes with it: its estimates, and whether one is priced or from chat. */
export interface ProjectDeleteFacts {
  estimates: number | null;
  /** A priced or chat estimate: the user types the project name to confirm. */
  needsName: boolean;
}

/**
 * Counts the project's estimates (the overview, else its imports list). Unknown stays null and
 * then asks for the name, the safe side.
 */
export async function projectDeleteFacts(projectId: string): Promise<ProjectDeleteFacts> {
  try {
    const o = await ProjectOverviewAPI.getOverview(projectId);
    if (o.estimates.length > 0) {
      return { estimates: o.estimates.length, needsName: o.estimates.some((e) => e.source === 'chat' || e.total != null) };
    }
  } catch {
    // older server: fall through to the imports list
  }
  try {
    const rows = await BackendAPI.listImports(projectId);
    const list = Array.isArray(rows) ? rows : [];
    return { estimates: list.length, needsName: list.some((r) => (r.parse_hash ?? '').startsWith('mcp-')) };
  } catch {
    return { estimates: null, needsName: true };
  }
}

/**
 * The dashboard's project delete: ask (naming the estimates that go with it), then hand the
 * project to `onConfirmed`, which starts the Undo window. Render `dialog` once.
 */
export function useProjectDeleteDialog(onConfirmed: (_p: Project) => void): {
  ask: (_p: Project) => void;
  dialog: JSX.Element | null;
} {
  const [asking, setAsking] = useState<{ p: Project; facts: ProjectDeleteFacts | null } | null>(null);
  const ask = (p: Project) => {
    setAsking({ p, facts: null });
    void projectDeleteFacts(p.id).then((facts) =>
      setAsking((cur) => (cur && cur.p.id === p.id ? { p, facts } : cur)),
    );
  };
  if (!asking) return { ask, dialog: null };
  const { p, facts } = asking;
  const name = getProjectDisplayName(p);
  const n = facts?.estimates;
  const what =
    n == null ? 'its estimates, notes and photos' : `its ${n} estimate${n === 1 ? '' : 's'}, notes and photos`;
  return {
    ask,
    dialog: (
      <ConfirmDeleteDialog
        title="Delete project?"
        typedName={facts?.needsName ? name : null}
        waiting={!facts}
        onClose={() => setAsking(null)}
        onConfirm={() => {
          setAsking(null);
          onConfirmed(p);
        }}
      >
        <b className="text-[var(--tx-hi)]">{name}</b> and {what} will be deleted.
      </ConfirmDeleteDialog>
    ),
  };
}

/**
 * The dashboard's whole project delete (W2.8): ask, hide the row, offer Undo for 10 s, then
 * `send(id)`. The row is shown again after the send settles (the caller's store has dropped it,
 * or put it back if the delete failed) or on Undo. Render `elements` once.
 */
export function useUndoableProjectDelete(send: (_id: string) => Promise<unknown>): {
  ask: (_p: Project) => void;
  hiddenIds: Set<string>;
  elements: JSX.Element;
} {
  const [hiddenIds, setHiddenIds] = useState<Set<string>>(() => new Set());
  const hide = (id: string, on: boolean) =>
    setHiddenIds((prev) => {
      const next = new Set(prev);
      if (on) next.add(id);
      else next.delete(id);
      return next;
    });
  const { schedule, toast } = useUndoableDelete();
  const { ask, dialog } = useProjectDeleteDialog((p) => {
    hide(p.id, true);
    schedule({
      label: getProjectDisplayName(p),
      send: () => Promise.resolve(send(p.id)).finally(() => hide(p.id, false)),
      onUndo: () => hide(p.id, false),
      onFailed: () => hide(p.id, false),
    });
  });
  return { ask, hiddenIds, elements: <>{dialog}{toast}</> };
}
